import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { atsApplyAll, atsApplyFix, atsChat, atsDismissBooster, atsReopenBooster, atsUndo } from '../../lib/api';
import type { AtsBooster, AtsChange, AtsChatMessage, AtsPlan, AtsRecord } from '../../lib/api';
import { Btn, C, ModelChip, Pill, TypingDots } from './ui';

// The right side of the workspace. Coach: the Score plan to 80% (Apply all
// the ready fixes, the questions left, a Fix for each missing keyword), then
// the chat with one suggestion at a time — each a ready-made fix: tap the
// answers, see the exact new line, Apply; every change shows Before / After
// and can be undone. HR message: ready to copy or send on WhatsApp.
// Checklist: before you apply, to learn, things to fix, build plans, what the
// fact check held back, and the job's keywords.

const IMPACT = {
  high:   { label: 'High impact',   color: '#dc2626', bg: '#fef2f2' },
  medium: { label: 'Medium impact', color: '#ea580c', bg: '#fff7ed' },
  low:    { label: 'Low impact',    color: '#16a34a', bg: '#f0fdf4' },
};
const QUICK = ["What's missing?", 'Stronger summary', 'Make it 1 page', 'Is this resume ready?'];
const ANSWER = '{answer}';

// "pivot tables", "pivot tables and VLOOKUP", "pivot tables, VLOOKUP and charts"
const joinAnswers = (a: string[]) => (a.length <= 1 ? a[0] || '' : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

// A preview line with the tapped answers (or "…") where {answer} stands
const Filled: React.FC<{ text: string; answers: string[] }> = ({ text, answers }) => {
  const parts = text.split(ANSWER);
  return (
    <>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {p}
          {i < parts.length - 1 && (answers.length
            ? <mark className="bg-yellow-200 text-inherit rounded px-0.5">{joinAnswers(answers)}</mark>
            : <span className="inline-block px-1.5 rounded bg-gray-200 text-gray-500">…</span>)}
        </React.Fragment>
      ))}
    </>
  );
};

// Lines a change writes: where, the old line crossed out, the new one
const ChangeList: React.FC<{ items: AtsChange[]; answers?: string[]; limit?: number }> = ({ items, answers = [], limit = 99 }) => {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, limit);
  return (
    <div className="space-y-1.5">
      {shown.map((c, i) => (
        <div key={i} className="rounded-xl bg-[#faf9fc] border border-gray-100 px-2.5 py-2">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">{c.where}</p>
          {c.before && <p className="text-xs text-gray-400 line-through decoration-red-300 mt-0.5">{c.before}</p>}
          <p className="text-xs text-gray-900 mt-0.5">{c.after ? <Filled text={c.after} answers={answers} /> : <span className="text-red-600 font-bold">Removed</span>}</p>
        </div>
      ))}
      {items.length > shown.length && <button onClick={() => setAll(true)} className="text-[11px] font-extrabold text-purple-700">See all {items.length} changes</button>}
    </div>
  );
};

const Avatar: React.FC<{ small?: boolean }> = ({ small }) => (
  <span className={`${small ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'} rounded-full shrink-0 flex items-center justify-center text-white shadow-md`}
    style={{ background: `linear-gradient(135deg, ${C.purple2}, ${C.gold})` }}>✨</span>
);

const Bubble: React.FC<{ m: AtsChatMessage; canUndo: boolean; busy: boolean; onUndo: () => void }> = ({ m, canUndo, busy, onUndo }) => {
  const mine = m.role === 'admin';
  const up = m.scoreFrom != null && m.scoreTo != null && m.scoreTo > m.scoreFrom;
  if (mine) {
    return (
      <div className="flex justify-end ats-fade">
        <div className="max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2 text-sm text-white whitespace-pre-wrap shadow-sm" style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.purple2})` }}>{m.text}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-2 items-end ats-fade">
      <Avatar small />
      <div className={`max-w-[88%] min-w-0 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm whitespace-pre-wrap border ${m.tag ? 'bg-amber-50/70 border-amber-100' : 'bg-white border-gray-100'} shadow-sm text-gray-800 ${m.undone ? 'opacity-60' : ''}`}>
        {m.tag && <p className="text-[10px] font-extrabold text-amber-700 mb-1">{m.tag}</p>}
        {m.text}
        {m.changes && m.changes.length > 0 && <div className="mt-2 whitespace-normal"><ChangeList items={m.changes} limit={2} /></div>}
        {m.scoreFrom != null && m.scoreTo != null && (
          <p className={`mt-2 inline-flex items-center gap-1 text-xs font-extrabold px-2 py-1 rounded-lg ${up ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
            📈 ATS score {m.scoreFrom}% → {m.scoreTo}%{up ? ' 🎉' : ''}
          </p>
        )}
        {m.undone && <p className="text-[11px] font-bold text-gray-500 mt-1.5">↩ Undone</p>}
        {canUndo && (
          <div className="mt-2">
            <button onClick={onUndo} disabled={busy} className="text-[11px] font-extrabold px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-purple-300 disabled:opacity-40">↩ Undo</button>
          </div>
        )}
        {m.note && <p className="text-[11px] mt-2 text-amber-800 bg-amber-50 rounded-lg px-2 py-1.5 whitespace-normal">🛡️ {m.note}</p>}
      </div>
    </div>
  );
};

// The current suggestion with its ready-made fix: tap the answers (a
// question), see the exact lines, Apply
const Suggestion = React.forwardRef<HTMLDivElement, { b: AtsBooster; blocked: boolean; onApply: (answers: string[], other: string) => void; onNo: () => void }>(
  ({ b, blocked, onApply, onNo }, ref) => {
    const [picked, setPicked] = useState<string[]>([]);
    const [otherOn, setOtherOn] = useState(false);
    const [other, setOther] = useState('');
    const imp = IMPACT[b.impact];
    const ask = b.kind === 'ask';
    const answers = [...picked, ...(other.trim() ? [other.trim()] : [])];
    const tap = (o: string) => setPicked(p => (p.includes(o) ? p.filter(x => x !== o) : b.multi ? [...p, o] : [o]));
    const gain = b.scoreAfter - b.scoreNow;
    return (
      <div ref={ref} className="flex gap-2 items-end ats-pop" data-suggestion={b.id}>
        <Avatar small />
        <div className="flex-1 min-w-0 rounded-2xl rounded-bl-md p-4 border-2 bg-white shadow-md" style={{ borderColor: `${imp.color}40` }}>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: imp.bg, color: imp.color }}>
              💡 Suggestion{b.of > 1 ? ` ${b.step} of ${b.of}` : ''}
            </span>
            {gain > 0 && <span className="text-[10px] font-extrabold px-2 py-1 rounded-full bg-green-50 text-green-700">{ask ? 'up to ' : ''}+{gain}% ATS</span>}
          </div>
          <p className="font-black text-gray-900 mt-2 leading-snug">{b.title}</p>
          {b.pitch && <p className="text-sm text-gray-600 mt-1">{b.pitch}</p>}

          {ask && (
            <div className="mt-3">
              <p className="text-sm font-extrabold" style={{ color: C.purple }}>{b.question}</p>
              <p className="text-[11px] text-gray-400">{b.options.length ? (b.multi ? 'Tap all that are true' : 'Tap one') : 'Type the answer'}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {b.options.map(o => {
                  const on = picked.includes(o);
                  return (
                    <button key={o} onClick={() => tap(o)} data-option={o}
                      className={`px-3 py-1.5 rounded-xl text-xs font-extrabold border-2 transition-all ${on ? 'text-white' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'}`}
                      style={on ? { background: C.purple, borderColor: C.purple } : {}}>
                      {on ? '✓ ' : ''}{o}
                    </button>
                  );
                })}
                <button onClick={() => setOtherOn(v => !v)} className={`px-3 py-1.5 rounded-xl text-xs font-extrabold border-2 border-dashed ${otherOn ? 'border-purple-400 text-purple-800' : 'border-gray-200 text-gray-500'}`}>＋ Other</button>
              </div>
              {(otherOn || !b.options.length) && (
                <input autoFocus value={other} onChange={e => setOther(e.target.value)} placeholder={b.detailHint || 'Type it exactly as it should appear'}
                  onKeyDown={e => { if (e.key === 'Enter' && answers.length) onApply(picked, other); }}
                  className="w-full mt-2 px-3 py-2 rounded-xl text-sm border-2 border-gray-200 bg-white focus:outline-none focus:border-purple-400" />
              )}
            </div>
          )}

          {b.preview.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-extrabold text-gray-500 mb-1.5">✍️ I will write:</p>
              <ChangeList items={b.preview} answers={answers} />
            </div>
          )}
          {!b.preview.length && b.instruction && <p className="text-xs text-gray-500 mt-2">✍️ {b.instruction}</p>}

          {gain > 0 && (
            <div className="mt-2.5 flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2">
              <span className="text-xs font-bold text-green-800">📈 ATS score</span>
              <span className="text-sm font-black text-gray-500">{b.scoreNow}%</span>
              <span className="text-green-600">→</span>
              <span className="text-sm font-black text-green-700">{b.scoreAfter}%</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            <Btn size="sm" kind={ask ? undefined : 'gold'} disabled={blocked || (ask && !answers.length)} onClick={() => onApply(picked, other)}
              style={ask ? { background: '#16a34a', color: '#fff', border: 'none' } : undefined}>
              {b.ready ? '✅ Apply' : ask ? '✅ Yes, add it' : '✨ Yes, do it'}
            </Btn>
            <Btn size="sm" disabled={blocked} onClick={onNo}>{ask ? '❌ No' : 'Skip'}</Btn>
          </div>
          {ask && b.ifNo && <p className="text-[11px] text-gray-400 mt-2">No? I'll show an honest way for the student to get it.</p>}
        </div>
      </div>
    );
  },
);

// 🎯 The Score plan: how far the ready fixes and the questions take it
const ScorePlan: React.FC<{ plan: AtsPlan; busy: boolean; onApplyAll: () => void; onStart: () => void; onFix: (term: string, boosterId: string) => void }> = ({ plan, busy, onApplyAll, onStart, onFix }) => {
  // Open while there's something to do
  const [open, setOpen] = useState(plan.now < plan.goal || plan.ready > 0 || plan.asks > 0 || plan.missing.length > 0);
  const reached = plan.now >= plan.goal;
  const notYet = plan.missing.filter(m => !m.covered);
  const bar = (v: number) => `${Math.max(0, Math.min(100, v))}%`;
  return (
    <div className="px-4 py-3 border-b border-gray-100 bg-white" data-plan>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 text-left">
        <span className="text-sm font-black text-gray-900">🎯 Goal {plan.goal}%</span>
        <span className={`text-xs font-extrabold ${reached ? 'text-green-700' : 'text-gray-500'}`}>{reached ? `🎉 Reached · ${plan.now}%` : `Now ${plan.now}%`}</span>
        <span className="ml-auto text-gray-400 text-xs">{open ? '▴' : '▾'}</span>
      </button>
      <div className="relative h-2.5 rounded-full bg-gray-100 mt-2 overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full bg-amber-100" style={{ width: bar(plan.max) }} />
        <div className="absolute inset-y-0 left-0 rounded-full bg-green-200" style={{ width: bar(plan.safe) }} />
        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-700" style={{ width: bar(plan.now), background: reached ? '#16a34a' : C.purple2 }} />
        <div className="absolute inset-y-0 w-0.5 bg-gray-900" style={{ left: bar(plan.goal) }} title={`Goal ${plan.goal}%`} />
      </div>
      {open && (
        <div className="mt-2.5 space-y-1.5 ats-fade">
          {plan.ready > 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-green-50 pl-3 pr-1.5 py-1.5" title="No question needed — only facts already in the resume">
              <p className="flex-1 text-xs text-green-900"><b>✅ {plan.ready} ready fix{plan.ready === 1 ? '' : 'es'}</b>{plan.safe > plan.now ? ` · +${plan.safe - plan.now}%` : ''}</p>
              <Btn size="sm" kind="gold" disabled={busy} onClick={onApplyAll}>✨ Apply all</Btn>
            </div>
          )}
          {plan.asks > 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-purple-50 pl-3 pr-1.5 py-1.5">
              <p className="flex-1 text-xs text-purple-950"><b>👆 {plan.asks} need{plan.asks === 1 ? 's' : ''} a tap from you</b>{plan.max > plan.safe ? ` · up to +${plan.max - plan.safe}%` : ''}</p>
              <Btn size="sm" kind="soft" disabled={busy} onClick={onStart}>Start</Btn>
            </div>
          )}
          {plan.missing.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <span className="text-[11px] font-extrabold text-gray-500">Missing:</span>
                {plan.missing.map(m => (
                  <span key={m.term} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-[11px] font-bold border bg-white"
                    style={{ borderColor: m.importance === 'must' ? '#fecaca' : '#e5e7eb', color: m.importance === 'must' ? '#b91c1c' : '#4b5563' }}>
                    {m.term}{m.importance === 'must' ? ' ★' : ''}{m.gain > 0 ? <span className="text-green-700"> +{m.gain}%</span> : ''}
                    <button onClick={() => onFix(m.term, m.boosterId)} disabled={busy} data-fix={m.term}
                      className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white disabled:opacity-40" style={{ background: C.purple }}>Fix</button>
                  </span>
                ))}
            </div>
          )}
          {!reached && plan.max < plan.goal && (
            <p className="text-[11px] text-amber-900 bg-amber-50 rounded-xl px-3 py-2">
              The highest honest score now is <b>{plan.max}%</b>.{notYet.length ? <> To pass {plan.goal}%, the student needs {notYet.slice(0, 3).map(m => m.term).join(', ')} — tap Fix to see how.</> : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const Block: React.FC<{ icon: string; title: string; tone: string; children: React.ReactNode }> = ({ icon, title, tone, children }) => (
  <div className="rounded-2xl p-4" style={{ background: tone }}>
    <p className="text-sm font-extrabold text-gray-900 mb-2">{icon} {title}</p>
    {children}
  </div>
);

type Tab = 'coach' | 'hr' | 'list';

const CoachPanel: React.FC<{ rec: AtsRecord; pw: string; dirty: boolean; onChanged: (r?: AtsRecord) => void }> = ({ rec, pw, dirty, onChanged }) => {
  const [tab, setTab] = useState<Tab>('coach');
  const [msg, setMsg] = useState('');
  const [currentId, setCurrentId] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [acting, setActing] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const result = useRef<HTMLDivElement>(null);
  const seen = useRef(rec.chat.length);
  const aiBusy = rec.busy === 'chat';
  const busy = aiBusy || acting;
  const open = rec.boosters.filter(b => b.status === 'open');
  const current = open.find(b => b.id === currentId) ?? open[0];
  const ex = rec.extras;
  const toLearn = ex?.toLearn ?? [];
  const listCount = (ex?.beforeApply?.length ?? 0) + (ex?.fixList.length ?? 0) + (ex?.buildPlans.length ?? 0) + toLearn.length;

  // A change just made: show its "Done!" from the top, the next suggestion
  // under it; otherwise the current suggestion from its top (or the end)
  const last = rec.chat[rec.chat.length - 1];
  const lastIsResult = !!last && last.role === 'ai' && (!!last.changes?.length || last.scoreTo != null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const grew = rec.chat.length > seen.current;
    seen.current = rec.chat.length;
    const target = (grew && result.current) || (!aiBusy && card.current) || null;
    el.scrollTop = target ? Math.max(0, target.offsetTop - 12) : el.scrollHeight;
  }, [rec.chat.length, aiBusy, current?.id, tab]);

  // An instant change (the server answers with the new resume), or one the
  // AI does in the background (then the page polls)
  const act = async (run: () => Promise<{ ok: true; resume?: AtsRecord }>) => {
    if (dirty) { toast.info('Save or discard your edits first'); return; }
    setActing(true);
    try {
      const res = await run();
      onChanged(res.resume);
    } catch (err: any) { toast.error(err.message); }
    finally { setActing(false); }
  };
  const say = (message: string) => act(async () => { const r = await atsChat(pw, rec._id, { message }); setMsg(''); return r; });
  const fix = (term: string, boosterId: string) => {
    if (boosterId && open.some(b => b.id === boosterId)) { setCurrentId(boosterId); setTab('coach'); return; }
    act(() => atsChat(pw, rec._id, { fixTerm: term }));
  };
  const firstAsk = () => { const b = open.find(x => x.kind === 'ask'); if (b) setCurrentId(b.id); };
  const copy = (t: string) => navigator.clipboard?.writeText(t).then(() => toast.success('Copied'), () => toast.info(t));

  const tabs: [Tab, string, number][] = [['coach', '🤖 Coach', open.length], ['hr', '✉️ HR message', 0], ['list', '✅ Checklist', listCount]];

  return (
    <div className="bg-white rounded-3xl border border-gray-100 ats-card flex flex-col overflow-hidden lg:max-h-[calc(100vh-8.5rem)]">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 text-white" style={{ background: `linear-gradient(135deg, ${C.purple}, #3d2f5f)` }}>
        <div className="flex items-center gap-3">
          <Avatar />
          <div className="flex-1 min-w-0">
            <p className="font-extrabold leading-tight">AI Coach</p>
            <p className="text-[11px] text-white/60">Ready-made fixes to get the interview call</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black leading-none" style={{ color: rec.scoreAfter >= 80 ? '#4ade80' : rec.scoreAfter >= 50 ? '#fbbf24' : '#f87171' }}>{rec.scoreAfter}%</p>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-wider">ATS · goal 80%</p>
          </div>
        </div>
        <div className="flex gap-1 mt-3 bg-white/10 rounded-xl p-1">
          {tabs.map(([k, label, n]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-extrabold transition-all ${tab === k ? 'bg-white text-[#2b2240] shadow' : 'text-white/70 hover:text-white'}`}>
              {label}{n > 0 && <span className={`ml-1 px-1.5 rounded-full text-[10px] ${tab === k ? 'bg-[#F0B429] text-[#2b2240]' : 'bg-white/20'}`}>{n}</span>}
            </button>
          ))}
        </div>
      </div>

      {tab === 'coach' && (
        <>
          {rec.plan && <ScorePlan plan={rec.plan} busy={busy || dirty}
            onApplyAll={() => act(() => atsApplyAll(pw, rec._id))} onStart={firstAsk} onFix={fix} />}
          <div ref={box} className="relative flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#faf9fc] min-h-[16rem]">
            {rec.chat.length === 0 && (
              <div className="flex gap-2 items-end">
                <Avatar small />
                <div className="rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm bg-white border border-gray-100 shadow-sm text-gray-700">
                  Your resume is ready! 🎉 Each suggestion below is a ready-made fix — tap Apply, or answer with a tap. You can also ask me for any change in plain words.
                </div>
              </div>
            )}
            {rec.chat.map((m, i) => (
              <div key={i} ref={lastIsResult && i === rec.chat.length - 1 ? result : undefined}>
                <Bubble m={m} canUndo={!!m.undoId && !m.undone && m.undoId === rec.undoId} busy={busy}
                  onUndo={() => act(() => atsUndo(pw, rec._id, m.undoId!))} />
              </div>
            ))}
            {aiBusy && (
              <div className="flex gap-2 items-end"><Avatar small /><div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white border border-gray-100 shadow-sm flex items-center gap-2 text-xs text-gray-500"><TypingDots /> The AI is working on it…</div></div>
            )}
            {!aiBusy && current && (
              <Suggestion ref={card} key={current.id} b={current} blocked={busy || dirty}
                onApply={(answers, other) => act(() => atsApplyFix(pw, rec._id, current.id, answers, other))}
                onNo={() => act(() => atsDismissBooster(pw, rec._id, current.id))} />
            )}
            {!aiBusy && !current && rec.chat.length > 0 && (
              <p className="text-xs text-gray-400 text-center py-1">No more suggestions right now. Tap Fix on a missing keyword, or ask me: “What will help get a call?”</p>
            )}
          </div>
          <div className="border-t border-gray-100 p-3 space-y-2.5">
            {open.length > 1 && !aiBusy && (
              <div>
                <button onClick={() => setShowAll(s => !s)} className="text-xs font-extrabold text-purple-700 hover:underline">
                  {showAll ? 'Hide the list' : `Show all ${open.length} suggestions`}
                </button>
                {showAll && (
                  <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                    {open.map(b => (
                      <button key={b.id} onClick={() => { setCurrentId(b.id); setShowAll(false); }}
                        className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs border ${b.id === current?.id ? 'border-purple-300 bg-purple-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <span style={{ color: IMPACT[b.impact].color }}>●</span>
                        <span className="flex-1 font-semibold text-gray-700">{b.title}</span>
                        {b.kind === 'ask' && <span className="text-[10px] font-bold text-purple-700">👆</span>}
                        {b.scoreAfter > b.scoreNow && <span className="font-extrabold text-green-700">+{b.scoreAfter - b.scoreNow}%</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {QUICK.map(q => (
                <button key={q} disabled={busy} onClick={() => say(q)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-bold border border-purple-200 text-purple-800 bg-white hover:bg-purple-50 disabled:opacity-40">{q}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={msg} onChange={e => setMsg(e.target.value)} disabled={busy}
                onKeyDown={e => { if (e.key === 'Enter' && msg.trim()) say(msg.trim()); }}
                placeholder="Ask for a change, or ask a question…"
                className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-100" />
              <Btn kind="dark" onClick={() => msg.trim() && say(msg.trim())} disabled={busy || !msg.trim()} aria-label="Send">➤</Btn>
            </div>
            <div className="flex items-center justify-between gap-2 text-[10px] text-gray-400">
              <ModelChip id={rec.models.chat} prefix="Coach: " />
              <span className="text-right">Facts are checked before any change goes in.</span>
            </div>
          </div>
        </>
      )}

      {tab === 'hr' && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {ex?.hrMessage ? (
            <>
              <p className="text-xs text-gray-500">Send this to the recruiter on WhatsApp, LinkedIn or email, with the resume.</p>
              <div className="rounded-2xl border border-gray-100 bg-[#faf9fc] p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{ex.hrMessage}</div>
              <div className="flex gap-2">
                <Btn kind="dark" onClick={() => copy(ex.hrMessage)}>📋 Copy</Btn>
                <a href={`https://wa.me/?text=${encodeURIComponent(ex.hrMessage)}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-extrabold text-white" style={{ background: '#25D366' }}>WhatsApp</a>
              </div>
              <p className="text-[11px] text-gray-400">{ex.hrMessage.split(/\s+/).filter(Boolean).length} words</p>
            </>
          ) : <p className="text-sm text-gray-400">No HR message for this resume.</p>}
        </div>
      )}

      {tab === 'list' && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {(ex?.beforeApply ?? []).length > 0 && (
            <Block icon="📋" title="Before you apply" tone="#eff6ff">
              <ul className="space-y-1.5">{ex!.beforeApply!.map((t, i) => <li key={i} className="text-sm text-blue-950 flex gap-2"><span>•</span><span>{t}</span></li>)}</ul>
            </Block>
          )}
          {toLearn.length > 0 && (
            <Block icon="🎓" title="To learn" tone="#fdf4ff">
              <div className="space-y-2">
                {toLearn.map(t => (
                  <div key={t.boosterId} className="rounded-xl bg-white/80 p-3" data-learn={t.boosterId}>
                    <p className="text-sm font-extrabold text-gray-900">{t.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{t.plan}</p>
                    <Btn size="sm" kind="soft" className="mt-2" disabled={busy}
                      onClick={() => act(async () => { const r = await atsReopenBooster(pw, rec._id, t.boosterId); setTab('coach'); return r; })}>✓ Learned it — add it now</Btn>
                  </div>
                ))}
              </div>
            </Block>
          )}
          {(ex?.fixList ?? []).length > 0 && (
            <Block icon="🧰" title="Fix these too" tone="#fffbeb">
              <ul className="space-y-1.5">{ex!.fixList.map((t, i) => <li key={i} className="text-sm text-amber-950 flex gap-2"><span>☐</span><span>{t}</span></li>)}</ul>
            </Block>
          )}
          {(ex?.buildPlans ?? []).map(p => (
            <Block key={p.id} icon="🛠️" title={`Build plan — ${p.project}`} tone="#f5f3ff">
              <p className="text-xs text-purple-900 mb-2">About {p.days} day{p.days === 1 ? '' : 's'}{p.tools.length ? ` · ${p.tools.join(', ')}` : ''}</p>
              <ol className="space-y-1.5">{p.steps.map((s, i) => (
                <li key={i} className="text-sm text-gray-800 flex gap-2"><span className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black text-white" style={{ background: C.purple2 }}>{i + 1}</span><span>{s}</span></li>
              ))}</ol>
            </Block>
          ))}
          {rec.fixes.length > 0 && (
            <Block icon="🛡️" title="Fact check — kept as in the resume" tone="#fff7ed">
              <ul className="space-y-1">{rec.fixes.map((f, i) => <li key={i} className="text-xs text-orange-950">• {f}</li>)}</ul>
            </Block>
          )}
          <Block icon="🔑" title={`Job keywords · ${rec.keywords.filter(k => k.now).length} of ${rec.keywords.length}`} tone="#f9fafb">
            <div className="flex flex-wrap gap-1.5">
              {rec.keywords.filter(k => k.now).map(k => <Pill key={k.term} bg={k.before ? '#dcfce7' : '#dbeafe'} color={k.before ? '#166534' : '#1e40af'}>{k.before ? '✓' : '↑'} {k.term}</Pill>)}
              {rec.keywords.filter(k => !k.now).map(k => <Pill key={k.term} bg="#f3f4f6" color="#6b7280">{k.term}{k.importance === 'must' ? ' ★' : ''}</Pill>)}
            </div>
            <p className="text-[11px] text-gray-400 mt-2">✓ was there · ↑ added by the new wording · grey: missing (add only if true) · ★ must-have</p>
          </Block>
          {listCount === 0 && rec.fixes.length === 0 && <p className="text-xs text-gray-400 text-center">Nothing else to do before applying.</p>}
        </div>
      )}
    </div>
  );
};

export default CoachPanel;
